"use client";

/**
 * AI SDK Elements — `tool`. Copiat al projecte amb la CLI de shadcn (no és un paquet):
 *   pnpm dlx shadcn@latest add https://registry.ai-sdk.dev/tool.json
 * Origen: https://github.com/vercel/ai-elements · registre `registry/default/ai-elements/tool.tsx`,
 * versió del registre del 14-SET-2026. Documentació: https://elements.ai-sdk.dev/docs
 *
 * Tres desviacions de l'original, totes marcades amb `[Guilleumes]` allà on són:
 *  1. Els dos `@ts-expect-error "state only available in AI SDK v6"` de `getStatusBadge` s'han
 *     tret: aquí `ai` és la v6.0.59, `approval-requested` ja és un estat vàlid i una directiva
 *     `@ts-expect-error` que no tapa cap error és error ella mateixa (TS2578).
 *  2. Els textos dels estats són en català, com la resta del xat.
 *  3. `ToolInput` no crida `CodeBlock` amb `input` indefinit (bug obert #490 d'ai-elements:
 *     `JSON.stringify(undefined)` torna `undefined` i Shiki peta amb un `code` que no és text).
 * A més, `Tool` afegeix la classe `group`, que l'original dóna per feta al `group-data-[state=open]`
 * de la fletxa del `ToolHeader` (sense ella la fletxa no gira mai).
 *
 * Si actualitzes aquest fitxer des del registre, torna a aplicar les quatre coses.
 */

import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import type { ToolUIPart } from "ai";
import {
  CheckCircleIcon,
  ChevronDownIcon,
  CircleIcon,
  ClockIcon,
  WrenchIcon,
  XCircleIcon,
} from "lucide-react";
import type { ComponentProps, ReactNode } from "react";
import { isValidElement } from "react";
import { CodeBlock } from "./code-block";

export type ToolProps = ComponentProps<typeof Collapsible>;

export const Tool = ({ className, ...props }: ToolProps) => (
  <Collapsible
    // [Guilleumes] `group`: la fletxa del `ToolHeader` gira amb `group-data-[state=open]`,
    // i l'estat `data-state` el posa aquest `Collapsible`.
    className={cn("group not-prose mb-4 w-full rounded-md border", className)}
    {...props}
  />
);

export type ToolHeaderProps = {
  title?: string;
  type: ToolUIPart["type"];
  state: ToolUIPart["state"];
  className?: string;
};

const getStatusBadge = (status: ToolUIPart["state"]) => {
  // [Guilleumes] textos en català i sense els `@ts-expect-error` de l'original (v6).
  const labels: Record<ToolUIPart["state"], string> = {
    "input-streaming": "Preparant",
    "input-available": "Executant",
    "approval-requested": "Esperant permís",
    "approval-responded": "Resposta donada",
    "output-available": "Feta",
    "output-error": "Error",
    "output-denied": "Denegada",
  };

  const icons: Record<ToolUIPart["state"], ReactNode> = {
    "input-streaming": <CircleIcon className="size-4" />,
    "input-available": <ClockIcon className="size-4 animate-pulse" />,
    "approval-requested": <ClockIcon className="size-4 text-yellow-600" />,
    "approval-responded": <CheckCircleIcon className="size-4 text-blue-600" />,
    "output-available": <CheckCircleIcon className="size-4 text-green-600" />,
    "output-error": <XCircleIcon className="size-4 text-red-600" />,
    "output-denied": <XCircleIcon className="size-4 text-orange-600" />,
  };

  return (
    <Badge className="gap-1.5 rounded-full text-xs" variant="secondary">
      {icons[status]}
      {labels[status]}
    </Badge>
  );
};

export const ToolHeader = ({
  className,
  title,
  type,
  state,
  ...props
}: ToolHeaderProps) => (
  <CollapsibleTrigger
    className={cn(
      "flex w-full items-center justify-between gap-4 p-3",
      className
    )}
    {...props}
  >
    <div className="flex items-center gap-2">
      <WrenchIcon className="size-4 text-muted-foreground" />
      <span className="font-medium text-sm">
        {title ?? type.split("-").slice(1).join("-")}
      </span>
      {getStatusBadge(state)}
    </div>
    <ChevronDownIcon className="size-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
  </CollapsibleTrigger>
);

export type ToolContentProps = ComponentProps<typeof CollapsibleContent>;

export const ToolContent = ({ className, ...props }: ToolContentProps) => (
  <CollapsibleContent
    className={cn(
      "data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2 text-popover-foreground outline-none data-[state=closed]:animate-out data-[state=open]:animate-in",
      className
    )}
    {...props}
  />
);

export type ToolInputProps = ComponentProps<"div"> & {
  input: ToolUIPart["input"];
};

export const ToolInput = ({ className, input, ...props }: ToolInputProps) => {
  // [Guilleumes] Guarda del bug #490: mentre l'estat és `input-streaming` l'`input` pot ser
  // `undefined`, i llavors `JSON.stringify` torna `undefined` (no una cadena) i `CodeBlock`
  // el passa a Shiki, que peta. Tampoc no s'hi arriba si el JSON no és serialitzable.
  let code: string | undefined;
  if (input !== undefined) {
    try {
      code = JSON.stringify(input, null, 2);
    } catch {
      code = undefined;
    }
  }

  return (
    <div className={cn("space-y-2 overflow-hidden p-4", className)} {...props}>
      <h4 className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
        Arguments
      </h4>
      {code === undefined ? (
        <p className="text-muted-foreground text-xs">preparant…</p>
      ) : (
        <div className="rounded-md bg-muted/50">
          <CodeBlock code={code} language="json" />
        </div>
      )}
    </div>
  );
};

export type ToolOutputProps = ComponentProps<"div"> & {
  output: ToolUIPart["output"];
  errorText: ToolUIPart["errorText"];
};

export const ToolOutput = ({
  className,
  output,
  errorText,
  ...props
}: ToolOutputProps) => {
  if (!(output || errorText)) {
    return null;
  }

  let Output = <div>{output as ReactNode}</div>;

  if (typeof output === "object" && !isValidElement(output)) {
    Output = (
      <CodeBlock code={JSON.stringify(output, null, 2)} language="json" />
    );
  } else if (typeof output === "string") {
    Output = <CodeBlock code={output} language="json" />;
  }

  return (
    <div className={cn("space-y-2 p-4", className)} {...props}>
      <h4 className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
        {errorText ? "Error" : "Resultat"}
      </h4>
      <div
        className={cn(
          "overflow-x-auto rounded-md text-xs [&_table]:w-full",
          errorText
            ? "bg-destructive/10 text-destructive"
            : "bg-muted/50 text-foreground"
        )}
      >
        {errorText && <div>{errorText}</div>}
        {Output}
      </div>
    </div>
  );
};
